import { openIndexedDb, transactionDone } from '../utils/idb';

const DB_NAME = 'family-messenger-system-events';
const DB_VERSION = 1;
const STORE_NAME = 'system_events';

export type StoredSystemEvent = {
  eventId: string;
  serverId: string;
  recipientIdentityId: string;
  type: string;
  payload: any;
  serverTimestamp: number;
};

function openDB(): Promise<IDBDatabase> {
  return openIndexedDb(DB_NAME, DB_VERSION, (db) => {
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'eventId' });
      store.createIndex('byTimestamp', 'serverTimestamp');
      store.createIndex('byRecipient', 'recipientIdentityId');
      store.createIndex('byType', 'type');
    }
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T>
): Promise<T> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    fn(store)
      .then(resolve)
      .catch(reject);
    transactionDone(tx).finally(() => db.close());
  });
}

export async function saveSystemEvents(events: StoredSystemEvent[]): Promise<void> {
  await withStore('readwrite', (store) => {
    return new Promise<void>((resolve, reject) => {
      for (const ev of events) {
        store.put(ev);
      }

      const tx = store.transaction;
      transactionDone(tx)
        .then(() => {
          window.dispatchEvent(new Event('system-events:updated'));
          resolve();
        })
        .catch(reject);
    });
  });
}

export async function deleteSystemEventsForIdentity(identityId: string): Promise<void> {
  const target = (identityId || '').trim();
  if (!target) return;
  await withStore('readwrite', (store) => new Promise<void>((resolve, reject) => {
    const request = store.index('byRecipient').openCursor(IDBKeyRange.only(target));
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        resolve();
        return;
      }
      cursor.delete();
      cursor.continue();
    };
  }));
  window.dispatchEvent(new Event('system-events:updated'));
}
